import React, { useState, useEffect } from 'react';
import { Save, Shield, Bell, Lock, Globe, Mail } from 'lucide-react';
import { settingAPI } from '../../../../services/dashboardAPI';

const SettingsSection = ({ showNotification }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    siteName: 'IHN Network',
    siteEmail: 'info@ihnnetwork.com',
    twoFactorAuth: false,
    emailNotifications: true,
    maintenanceMode: false,
    autoBackup: true,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingAPI.getAll();
      if (response.success) {
        const settings = {};
        response.data.forEach(setting => {
          settings[setting.key] = setting.type === 'boolean' ? setting.value === 'true' : setting.value;
        });
        setFormData(prev => ({ ...prev, ...settings }));
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToggle = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      const keys = Object.keys(formData);
      
      for (let key of keys) {
        const response = await settingAPI.set(
          key,
          String(formData[key]),
          `Setting for ${key}`,
          'general',
          typeof formData[key] === 'boolean' ? 'boolean' : 'string'
        );
        
        if (!response.success) {
          showNotification(`Failed to save ${key}`, 'error');
          return;
        }
      }
      
      showNotification('Settings saved successfully!');
      fetchSettings();
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="section-header">
        <div>
          <h2 className="title">Settings</h2>
          <p className="subtitle">Manage system settings and preferences</p>
        </div>
        <button className="btn-primary" onClick={handleSaveSettings} disabled={loading}>
          <Save size={18} /> {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <div className="settings-container">
        <div className="settings-section">
          <div className="settings-section-header">
            <Globe size={20} />
            <h3>General Settings</h3>
          </div>
          
          <div className="form-group">
            <label>Site Name</label>
            <input
              type="text"
              value={formData.siteName}
              onChange={(e) => handleChange('siteName', e.target.value)}
              placeholder="Enter site name"
            />
          </div>

          <div className="form-group">
            <label>Site Email</label>
            <input
              type="email"
              value={formData.siteEmail}
              onChange={(e) => handleChange('siteEmail', e.target.value)}
              placeholder="Enter site email"
            />
          </div>

          <div className="form-group toggle-group">
            <label>Maintenance Mode</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={formData.maintenanceMode}
                onChange={() => handleToggle('maintenanceMode')}
                id="maintenance"
              />
              <label htmlFor="maintenance" className="switch"></label>
              <span>{formData.maintenanceMode ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>

          <div className="form-group toggle-group">
            <label>Auto Backup</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={formData.autoBackup}
                onChange={() => handleToggle('autoBackup')}
                id="autobackup"
              />
              <label htmlFor="autobackup" className="switch"></label>
              <span>{formData.autoBackup ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <div className="settings-section-header">
            <Shield size={20} />
            <h3>Security Settings</h3>
          </div>

          <div className="form-group toggle-group">
            <label>Two-Factor Authentication</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={formData.twoFactorAuth}
                onChange={() => handleToggle('twoFactorAuth')}
                id="2fa"
              />
              <label htmlFor="2fa" className="switch"></label>
              <span>{formData.twoFactorAuth ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>

          <div className="info-box">
            <Lock size={16} />
            <div>
              <p className="font-weight-bold">Password Policy</p>
              <p className="text-small">Minimum 8 characters with uppercase, lowercase, numbers and symbols</p>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <div className="settings-section-header">
            <Bell size={20} />
            <h3>Notification Settings</h3>
          </div>

          <div className="form-group toggle-group">
            <label>Email Notifications</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={formData.emailNotifications}
                onChange={() => handleToggle('emailNotifications')}
                id="emailnotif"
              />
              <label htmlFor="emailnotif" className="switch"></label>
              <span>{formData.emailNotifications ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>

          <div className="notification-options">
            <p className="text-small font-weight-bold">Email me about:</p>
            <div className="checkbox-list">
              <label><input type="checkbox" defaultChecked /> New user registrations</label>
              <label><input type="checkbox" defaultChecked /> Event bookings</label>
              <label><input type="checkbox" defaultChecked /> New gallery uploads</label>
              <label><input type="checkbox" defaultChecked /> System alerts</label>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsSection;
